(ns metabase.lib-metric.schema
  "Malli schemas for metric dimensions, dimension-mappings, and dimension-references."
  (:refer-clojure :exclude [some])
  (:require
   [metabase.lib-metric.operators :as operators]
   [metabase.lib.metadata.protocols :as lib.metadata.protocols]
   [metabase.lib.schema.common :as lib.schema.common]
   [metabase.lib.schema.id :as lib.schema.id]
   [metabase.lib.schema.literal :as lib.schema.literal]
   [metabase.lib.schema.metadata :as lib.schema.metadata]
   [metabase.lib.schema.ref :as lib.schema.ref]
   [metabase.lib.schema.temporal-bucketing :as lib.schema.temporal-bucketing]
   [metabase.util.malli.registry :as mr]
   [metabase.util.performance :refer [some]]))

(comment lib.schema.ref/keep-me)

;;; ------------------------------------------------- Binning -------------------------------------------------
;;; Duplicated from metabase.lib.schema.binning because lib-metric is an independent implementation.

(mr/def ::binning-strategy
  [:enum
   {:decode/normalize lib.schema.common/normalize-keyword}
   :bin-width :default :num-bins])

(mr/def ::binning
  "Schema for `:binning` options passed to a `:dimension` clause."
  [:and
   {:decode/normalize (fn [binning]
                        (when-some [binning (lib.schema.common/normalize-map binning)]
                          (cond-> binning
                            (:strategy binning) (update :strategy lib.schema.common/normalize-keyword))))}
   [:multi {:dispatch (fn [x]
                        (keyword (some #(get x %) [:strategy "strategy"])))
            :error/fn (fn [{:keys [value]} _]
                        (str "Invalid binning strategy" (pr-str value)))}
    [:default   [:map {:closed true}
                 [:strategy [:= :default]]]]
    [:bin-width [:map {:closed true}
                 [:strategy  [:= :bin-width]]
                 [:bin-width [:ref ::lib.schema.common/positive-number]]]]
    [:num-bins  [:map {:closed true}
                 [:strategy [:= :num-bins]]
                 [:num-bins pos-int?]]]]])

(mr/def ::binning-option
  "Schema for a binning option as presented to the UI."
  [:map {:closed true}
   [:lib/type [:= :option/binning]]
   [:display-name :string]
   [:mbql [:maybe ::binning]]
   [:default {:optional true} :boolean]
   [:selected {:optional true} :boolean]])

;;; -----------------------------------------------------------------------------------------------------------------

(mr/def ::dimension-id
  "UUID string identifying a dimension."
  ::lib.schema.metadata/dimension-id)

(mr/def ::dimension-group
  ::lib.schema.metadata/dimension-group)

(mr/def ::dimension-source.type
  ::lib.schema.metadata/dimension-source.type)

(mr/def ::dimension-source
  ::lib.schema.metadata/dimension-source)

(mr/def ::dimension
  "Schema for a dimension definition, plus what the metrics module annotates a dimension with before handing it to a
  client: the column it came from, its group, its field values status and how interesting it looks."
  [:map {:closed true}
   [:id                        ::dimension-id]
   [:display-name              {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   [:effective-type            {:optional true} [:maybe ::lib.schema.common/base-type]]
   [:semantic-type             {:optional true} [:maybe ::lib.schema.common/semantic-or-relation-type]]
   [:sources                   {:optional true} [:maybe [:sequential ::dimension-source]]]
   [:name                      {:optional true} [:maybe :string]]
   [:group                     {:optional true} [:maybe ::dimension-group]]
   [:lib/source                {:optional true} [:maybe [:or ::lib.schema.metadata/column.source :string]]]
   [:has-field-values          {:optional true} [:maybe ::lib.schema.metadata/column.has-field-values]]
   [:status                    {:optional true} [:maybe [:or :keyword :string]]]
   [:status-message            {:optional true} [:maybe :string]]
   [:default-temporal-unit     {:optional true} [:maybe ::lib.schema.temporal-bucketing/unit]]
   [:dimension-interestingness {:optional true} [:maybe number?]]
   [:default                   {:optional true} [:maybe :boolean]]
   [:description               {:optional true} [:maybe :string]]])

(mr/def ::dimension-mapping.type
  ::lib.schema.metadata/dimension-mapping.type)

(mr/def ::dimension-mapping.target
  ::lib.schema.metadata/dimension-mapping.target)

(mr/def ::dimension-mapping
  "Schema for a dimension mapping."
  [:map {:closed true}
   [:type         ::dimension-mapping.type]
   [:table-id     {:optional true} [:maybe ::lib.schema.id/table]]
   [:dimension-id ::dimension-id]
   [:target       ::dimension-mapping.target]])

(mr/def ::dimension-reference.options
  "Options map for dimension references."
  [:map
   {:decode/normalize lib.schema.common/normalize-options-map :closed true}
   [:lib/uuid       {:optional true} [:ref ::lib.schema.common/uuid]]
   [:display-name   {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   [:effective-type {:optional true} [:maybe ::lib.schema.common/base-type]]
   [:semantic-type  {:optional true} [:maybe ::lib.schema.common/semantic-or-relation-type]]
   [:temporal-unit  {:optional true} [:maybe ::lib.schema.temporal-bucketing/unit]]
   [:binning        {:optional true} [:maybe ::binning]]])

(mr/def ::dimension-reference
  "Dimension reference clause [:dimension opts uuid].
   Handles normalization from JSON where the tag arrives as a string."
  [:tuple
   {:decode/normalize (fn [x]
                        (when (and (sequential? x)
                                   (= 3 (count x)))
                          (let [[_tag opts dimension-id] x]
                            [:dimension
                             (or opts {})
                             dimension-id])))}
   [:= {:decode/normalize lib.schema.common/normalize-keyword} :dimension]
   ::dimension-reference.options
   ::dimension-id])

(mr/def ::dimension-or-reference
  "Dimension or a reference to a dimension."
  [:or
   ::dimension-reference
   ::metadata-dimension])

;;; ------------------------------------------------- Filter Clause Normalization -------------------------------------------------
;;; Filter clauses from the API arrive with string operators and dimension refs that need normalization.

(defn normalize-dimension-ref
  "Normalize a dimension reference from API format to internal format."
  [x]
  (when (and (sequential? x)
             (= 3 (count x))
             (let [tag (first x)]
               (or (= tag "dimension") (= tag :dimension))))
    (let [[_tag opts dimension-id] x]
      [:dimension
       (lib.schema.common/normalize-options-map (or opts {}))
       dimension-id])))

(defn- normalize-value-clause
  "Normalize a [:value opts literal] clause from API format."
  [x]
  (when (and (sequential? x)
             (= 3 (count x))
             (let [tag (first x)]
               (or (= tag "value") (= tag :value))))
    (let [[_tag opts literal] x
          norm-opts (lib.schema.common/normalize-options-map (or opts {}))]
      [:value
       (cond-> norm-opts
         (:base-type norm-opts)      (update :base-type lib.schema.common/normalize-keyword)
         (:effective-type norm-opts)  (update :effective-type lib.schema.common/normalize-keyword))
       literal])))

(defn normalize-filter-clause
  "Recursively normalize a filter clause from API format.
   Converts string operators to keywords and normalizes dimension references
   and value clauses."
  [clause]
  (when (sequential? clause)
    (let [[op opts & args] clause
          operator (lib.schema.common/normalize-keyword op)
          norm-opts (lib.schema.common/normalize-options-map (or opts {}))]
      (case operator
        ;; Compound filters - recursively normalize children
        (:and :or)
        (into [operator norm-opts] (map normalize-filter-clause args))

        :not
        [operator norm-opts (normalize-filter-clause (first args))]

        ;; All other filters - normalize dimension refs and value clauses in args
        (into [operator norm-opts]
              (map (fn [arg]
                     (or (normalize-dimension-ref arg)
                         (normalize-value-clause arg)
                         arg))
                   args))))))

(mr/def ::filter-clause.options
  "The options of a metric filter clause: MBQL options plus the offset [[metabase.lib-metric.filter]] puts on a
  relative date filter (`previous N units, starting M units ago`)."
  [:merge
   ::lib.schema.common/options
   [:map
    [:offset-unit  {:optional true} [:ref ::lib.schema.temporal-bucketing/unit]]
    [:offset-value {:optional true} :int]]])

(mr/def ::filter-clause
  "MBQL filter clause with normalization for API input.
   Handles string operators and dimension references. After normalization every clause is a tag, an options map and
   arguments that are dimension references, literals or clauses themselves."
  [:schema {:decode/normalize normalize-filter-clause}
   [:cat
    :keyword
    [:schema [:ref ::filter-clause.options]]
    [:* [:or
         [:schema [:ref ::dimension-reference]]
         [:schema [:ref ::lib.schema.literal/literal]]
         [:schema [:ref ::filter-clause]]]]]])

;;; ------------------------------------------------- Metric Math Expressions -------------------------------------------------
;;; Expression schemas for metric math: combining multiple metrics/measures with arithmetic.

(defn- normalize-expression-ref
  "Normalize an expression ref from API format: [\"metric\" {\"lib/uuid\" \"...\"} id] -> [:metric {:lib/uuid \"...\"} id]."
  [tag]
  (fn [x]
    (when (and (sequential? x)
               (= 3 (count x)))
      (let [[t opts id] x]
        (when (or (= t tag) (= t (name tag)))
          [tag
           (lib.schema.common/normalize-options-map (or opts {}))
           id])))))

(def ^:private normalize-metric-ref (normalize-expression-ref :metric))
(def ^:private normalize-measure-ref (normalize-expression-ref :measure))

(mr/def ::metric-expression-ref
  "A metric reference in an expression: [:metric {:lib/uuid uuid} card-id]."
  [:tuple
   {:decode/normalize normalize-metric-ref}
   [:= {:decode/normalize lib.schema.common/normalize-keyword} :metric]
   [:map {:decode/normalize lib.schema.common/normalize-options-map :closed true}
    [:lib/uuid ::lib.schema.common/non-blank-string]]
   pos-int?])

(mr/def ::measure-expression-ref
  "A measure reference in an expression: [:measure {:lib/uuid uuid} measure-id]."
  [:tuple
   {:decode/normalize normalize-measure-ref}
   [:= {:decode/normalize lib.schema.common/normalize-keyword} :measure]
   [:map {:decode/normalize lib.schema.common/normalize-options-map :closed true}
    [:lib/uuid ::lib.schema.common/non-blank-string]]
   pos-int?])

(mr/def ::expression-leaf
  "A leaf node in a metric math expression: either a metric or measure reference."
  [:or ::metric-expression-ref ::measure-expression-ref])

(mr/def ::arithmetic-operator
  "Arithmetic operators for metric math."
  (into [:enum {:decode/normalize lib.schema.common/normalize-keyword}]
        (operators/arithmetic-operator-keywords)))

(defn normalize-math-expression
  "Recursively normalize a metric math expression from API format.
   Handles string keys, string operators, nested expressions, and bare numeric constants."
  [x]
  (cond
    (number? x) x
    (sequential? x)
    (let [[first-el] x]
      (if (and (>= (count x) 3)
               (let [tag (lib.schema.common/normalize-keyword first-el)]
                 (or (= tag :metric) (= tag :measure))))
        ;; It's a leaf ref - normalize it
        ((normalize-expression-ref (lib.schema.common/normalize-keyword first-el)) x)
        ;; It's an arithmetic expression: [op opts & exprs]
        (when (>= (count x) 4)
          (let [[op opts & exprs] x]
            (into [(lib.schema.common/normalize-keyword op)
                   (lib.schema.common/normalize-options-map (or opts {}))]
                  (map normalize-math-expression exprs))))))
    :else nil))

(mr/def ::metric-math-expression
  "A recursive metric math expression tree.
   Can be a leaf (metric/measure ref) or an arithmetic expression [op opts expr expr ...]
   with at least 2 operands.
   Note: uses :fn validator for arithmetic to avoid Malli's recursive seqex limitation."
  [:schema
   {:decode/normalize normalize-math-expression}
   [:or
    ::expression-leaf
    number?
    [:and
     vector?
     [:fn {:error/message "must be arithmetic expression [op opts expr expr ...] with at least 2 operands"}
      (fn [x]
        (and (>= (count x) 4)
             (operators/arithmetic? (first x))
             (map? (second x))))]]]])

;;; ------------------------------------------------- Per-Instance Filters -------------------------------------------------
;;; Filters keyed by lib/uuid from the expression, for independent filtering per instance.

(mr/def ::instance-filter
  "A filter associated with a specific expression instance via lib/uuid."
  [:map
   {:decode/normalize lib.schema.common/normalize-map :closed true}
   [:lib/uuid ::lib.schema.common/non-blank-string]
   [:filter   ::filter-clause]])

(mr/def ::instance-filters
  "A sequence of per-instance filters."
  [:sequential ::instance-filter])

;;; ------------------------------------------------- Typed Projections -------------------------------------------------
;;; Projections keyed by :lib/uuid from the expression leaf, with type and ID for metadata resolution.

(mr/def ::typed-projection
  "A projection associated with a specific expression leaf instance via :lib/uuid.
   The :type and :id identify the source metric/measure for metadata resolution.
   The :lib/uuid disambiguates multiple references to the same metric/measure in an expression."
  [:map
   {:decode/normalize lib.schema.common/normalize-map :closed true}
   [:type       [:enum {:decode/normalize lib.schema.common/normalize-keyword} :metric :measure]]
   [:id         pos-int?]
   [:lib/uuid   ::lib.schema.common/non-blank-string]
   [:projection [:sequential ::dimension-reference]]])

(mr/def ::typed-projections
  "A sequence of typed projections."
  [:sequential ::typed-projection])

;;; ------------------------------------------------- Persisted Dimensions -------------------------------------------------
;;; These schemas are used for storage format in the database.

(mr/def ::dimension-status
  ::lib.schema.metadata/dimension-status)

(mr/def ::persisted-dimension
  "Schema for a persisted dimension definition with status tracking."
  [:map {:closed true}
   [:id               ::dimension-id]
   [:name             {:optional true} [:maybe :string]]
   [:display-name     {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   [:description      {:optional true} [:maybe :string]]
   [:effective-type   {:optional true} [:maybe ::lib.schema.common/base-type]]
   [:semantic-type    {:optional true} [:maybe ::lib.schema.common/semantic-or-relation-type]]
   [:has-field-values {:optional true} [:maybe [:enum :list :search :none]]]
   [:status           {:optional true} [:maybe ::dimension-status]]
   [:status-message   {:optional true} [:maybe :string]]
   [:sources          {:optional true} [:maybe [:sequential ::dimension-source]]]
   [:group            {:optional true} [:maybe ::dimension-group]]
   [:lib/source       {:optional true} [:maybe [:or ::lib.schema.metadata/column.source :string]]]
   [:default-temporal-unit {:optional true} ::lib.schema.temporal-bucketing/unit]
   [:default          {:optional true} [:maybe :boolean]]])

(mr/def ::persisted-dimensions
  "Schema for a sequence of persisted dimensions."
  [:sequential ::persisted-dimension])

;;; ------------------------------------------------- MetricDefinition -------------------------------------------------

(mr/def ::metric-definition
  "A MetricDefinition represents an exploration of a metric or measure.
   - expression: a metric math expression tree (leaf ref or arithmetic)
   - filters: per-instance filters keyed by :lib/uuid from the expression
   - projections: typed projections keyed by source type and ID
   - metadata-provider: for resolving additional metadata

   Metadata is loaded lazily from the provider in the AST builder,
   not stored in the definition."
  [:map {:closed true}
   [:lib/type          [:= :metric/definition]]
   [:expression        ::metric-math-expression]
   [:filters           ::instance-filters]
   [:projections       ::typed-projections]
   [:metadata-provider [:maybe ::lib.metadata.protocols/metadata-providerable]]])

;;; ------------------------------------------------- Fetchable Dimension Metadata -------------------------------------------------
;;; These schemas support dimensions as first-class metadata entities
;;; fetchable through the MetricContextMetadataProvider.

(mr/def ::dimension-source-type
  "Source type indicating whether a dimension comes from a metric or measure."
  [:enum :metric :measure])

(mr/def ::metadata-dimension
  "Schema for dimension metadata fetchable via metadata provider.
   Dimensions are extracted from metrics/measures at fetch time, with source
   tracking to identify their parent entity."
  [:map {:closed true}
   [:lib/type         [:= :metadata/dimension]]
   [:id               ::dimension-id]  ; UUID string
   [:name             {:optional true} [:maybe :string]]
   [:display-name     {:optional true} [:maybe ::lib.schema.common/non-blank-string]]
   [:description      {:optional true} [:maybe :string]]
   [:effective-type   {:optional true} [:maybe ::lib.schema.common/base-type]]
   [:semantic-type    {:optional true} [:maybe ::lib.schema.common/semantic-or-relation-type]]
   [:has-field-values {:optional true} [:maybe [:enum :list :search :none]]]
   [:status           {:optional true} [:maybe ::dimension-status]]
   [:status-message   {:optional true} [:maybe :string]]
   [:sources          {:optional true} [:maybe [:sequential ::dimension-source]]]
   [:group            {:optional true} [:maybe ::dimension-group]]
   [:lib/source       {:optional true} [:maybe [:or ::lib.schema.metadata/column.source :string]]]
   [:default-temporal-unit {:optional true} ::lib.schema.temporal-bucketing/unit]
   [:default          {:optional true} [:maybe :boolean]]
   ;; Source tracking
   [:source-type      ::dimension-source-type]
   [:source-id        pos-int?]
   ;; Optional mapping for field resolution
   [:dimension-mapping {:optional true} [:maybe ::dimension-mapping]]
   [:projection-positions {:optional true} [:maybe [:sequential :int]]]])

(mr/def ::dimension-spec
  "Spec for fetching dimensions from metadata provider.
   At least one filter should be provided for efficient querying."
  [:map
   [:lib/type   [:= :metadata/dimension]]
   [:id         {:optional true} [:set {:min 1} ::lib.schema.common/uuid]]
   [:metric-id  {:optional true} pos-int?]
   [:measure-id {:optional true} pos-int?]
   [:table-id   {:optional true} pos-int?]])

;;; ------------------------------------------------- Computed Dimensions -------------------------------------------------
;;; Schemas for computed dimensions during reconciliation.

(mr/def ::computed-dimension
  "A dimension computed from a visible column, before reconciliation.
   The :id is nil until assigned during reconciliation."
  [:map {:closed true}
   [:id [:maybe ::dimension-id]]
   [:name :string]
   [:display-name {:optional true} [:maybe :string]]
   [:effective-type {:optional true} [:maybe :keyword]]
   [:semantic-type {:optional true} [:maybe :keyword]]
   [:has-field-values {:optional true} [:maybe [:enum :list :search :none]]]
   [:lib/source {:optional true} [:maybe :keyword]]
   [:sources {:optional true} [:maybe [:sequential ::dimension-source]]]
   [:group {:optional true} [:maybe ::dimension-group]]])

(mr/def ::computed-pair
  "A computed dimension paired with its mapping (before ID assignment)."
  [:map {:closed true}
   [:dimension ::computed-dimension]
   [:mapping [:map {:closed true}
              [:type ::dimension-mapping.type]
              [:table-id {:optional true} [:maybe ::lib.schema.id/table]]
              [:target ::dimension-mapping.target]]]])

;;; ------------------------------------------------- Display Info -------------------------------------------------

(mr/def ::display-info
  "Schema for display info returned by display-info function."
  [:map
   [:display-name {:optional true} :string]
   [:name {:optional true} :string]
   [:long-display-name {:optional true} :string]
   [:effective-type {:optional true} :keyword]
   [:semantic-type {:optional true} :keyword]
   [:description {:optional true} [:maybe :string]]
   [:selected {:optional true} :boolean]
   [:default {:optional true} :boolean]
   [:short-name {:optional true} :string]
   ;; Position tracking for dimensions
   [:filter-positions {:optional true} [:sequential :int]]
   [:projection-positions {:optional true} [:sequential :int]]
   ;; Dimension group
   [:group {:optional true} [:maybe ::dimension-group]]
   ;; Source indicators
   [:is-from-join {:optional true} :boolean]
   [:is-calculated {:optional true} :boolean]
   [:is-implicitly-joinable {:optional true} :boolean]
   ;; Temporal bucket specific
   [:is-temporal-extraction {:optional true} :boolean]])
